import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { Dash, AgeCell, PriorityCell, StatusPill, UnassignedCell, formatDate } from "@/components/board/boardFormat";

const MENU_HEIGHT = 300;

function RowMenu({ archived, canDelete, canManage, row, onAction }) {
  const [position, setPosition] = useState(null);
  const ref = useRef(null);
  const listRef = useRef(null);
  const open = Boolean(position);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => !ref.current?.contains(event.target) && !listRef.current?.contains(event.target) && setPosition(null);
    const dismiss = () => setPosition(null);
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [open]);
  function toggle() {
    if (open) {
      setPosition(null);
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    const openUp = window.innerHeight - rect.bottom < MENU_HEIGHT && rect.top > MENU_HEIGHT;
    setPosition({
      right: Math.max(8, window.innerWidth - rect.right),
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }
  const pick = (action, payload) => { setPosition(null); onAction(row, action, payload); };
  return (
    <div className="row-menu" ref={ref} onClick={(event) => event.stopPropagation()}>
      <button aria-haspopup="menu" aria-expanded={open} aria-label={`Actions for ${row.id}`} className="row-menu-button" data-testid={`row-menu-${row.id}`} type="button" onClick={toggle}>
        <MoreHorizontal size={16} />
      </button>
      {open && createPortal(
        <div className="row-menu-list row-menu-portal" data-testid={`row-menu-list-${row.id}`} ref={listRef} role="menu" style={position} onClick={(event) => event.stopPropagation()}>
          {archived ? (
            <>
              <button role="menuitem" data-testid={`row-menu-restore-${row.id}`} type="button" onClick={() => pick("restore")}>Restore to board</button>
              <button className="row-menu-danger" role="menuitem" data-testid={`row-menu-purge-${row.id}`} type="button" onClick={() => pick("purge")}>Delete permanently</button>
            </>
          ) : (
            <>
              <button role="menuitem" data-testid={`row-menu-view-${row.id}`} type="button" onClick={() => pick("view")}>View details</button>
              {canManage && (
                <>
                  <button role="menuitem" data-testid={`row-menu-recruiter-${row.id}`} type="button" onClick={() => pick("recruiter")}>Assign recruiter</button>
                  <button role="menuitem" data-testid={`row-menu-candidate-${row.id}`} type="button" onClick={() => pick("fill")}>Assign candidate</button>
                  {row.status === "Filled" && <button role="menuitem" data-testid={`row-menu-vacate-${row.id}`} type="button" onClick={() => pick("vacate")}>Vacate position</button>}
                  <button role="menuitem" data-testid={`row-menu-client-${row.id}`} type="button" onClick={() => pick("client")}>Link client</button>
                  <span className="row-menu-group">Set priority</span>
                  {["Urgent", "High", "Standard"].filter((value) => value !== row.priority).map((value) => (
                    <button key={value} role="menuitem" data-testid={`row-menu-priority-${value.toLowerCase()}-${row.id}`} type="button" onClick={() => pick("priority", value)}>{value}</button>
                  ))}
                </>
              )}
              <button role="menuitem" data-testid={`row-menu-copy-${row.id}`} type="button" onClick={() => pick("copy")}>Copy request ID</button>
              {canDelete && (
                <button role="menuitem" data-testid={`row-menu-archive-${row.id}`} type="button" onClick={() => pick("archive")}>Archive request</button>
              )}
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

export const BoardTable = ({ archived = false, canDelete = false, canManage, rows, selected, onAction, onOpen, onToggle, onToggleAll }) => {
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));
  const selectable = canManage;
  const open = (row) => !archived && onOpen(row);
  return (
    <section className={`board-table-card ${archived ? "board-table-archived" : ""}`} data-testid={archived ? "staffing-board-archived-table" : "staffing-board-table"}>
      <div className="board-table-scroll">
        <table className="board-table">
          <thead>
            <tr>
              {selectable && (
                <th className="board-check-cell">
                  <input aria-label="Select all requests" checked={allSelected} data-testid="board-select-all" type="checkbox" onChange={onToggleAll} />
                </th>
              )}
              <th>Request ID</th>
              <th>Client</th>
              <th>Role</th>
              {archived ? (
                <>
                  <th>Status when archived</th>
                  <th>Archived</th>
                  <th>Archived by</th>
                  <th>Reason</th>
                  <th>Assignments</th>
                </>
              ) : (
                <>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Recruiter</th>
                  <th>Candidate</th>
                  <th>Date needed</th>
                  <th>Age</th>
                  <th>Last updated</th>
                </>
              )}
              <th className="board-menu-cell" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                className={`board-row ${archived ? "board-row-static" : ""} ${selected.has(row.id) ? "board-row-selected" : ""}`}
                data-testid={`board-row-${row.id}`}
                key={row.id}
                tabIndex={0}
                onClick={() => open(row)}
                onKeyDown={(event) => event.key === "Enter" && open(row)}
              >
                {selectable && (
                  <td className="board-check-cell" onClick={(event) => event.stopPropagation()}>
                    <input aria-label={`Select ${row.id}`} checked={selected.has(row.id)} data-testid={`board-select-${row.id}`} type="checkbox" onChange={() => onToggle(row.id)} />
                  </td>
                )}
                <td>
                  {archived ? (
                    <span className="request-id-link" data-testid={`board-request-link-${row.id}`}>{row.id}</span>
                  ) : (
                    <button className="request-id-link" data-testid={`board-request-link-${row.id}`} type="button" onClick={(event) => { event.stopPropagation(); onOpen(row); }}>{row.id}</button>
                  )}
                </td>
                <td>{row.client_id ? <span data-testid={`board-client-${row.id}`}>{row.client_name}</span> : <UnassignedCell label="No client linked" testId={`board-client-${row.id}`} value="" />}</td>
                <td>{row.role_name || <Dash />}</td>
                {archived ? (
                  <>
                    <td><StatusPill status={row.status} testId={`board-status-${row.id}`} /></td>
                    <td data-testid={`board-archived-at-${row.id}`}>{formatDate(row.archived_at, true)}</td>
                    <td data-testid={`board-archived-by-${row.id}`}>{row.archived_by_name || <Dash />}</td>
                    <td className="board-archive-reason" data-testid={`board-archive-reason-${row.id}`}>{row.archive_reason || <Dash />}</td>
                    <td data-testid={`board-archived-assignments-${row.id}`}>{row.archived_assignment_count ? `${row.archived_assignment_count} archived` : <Dash />}</td>
                  </>
                ) : (
                  <>
                    <td><PriorityCell priority={row.priority} testId={`board-priority-${row.id}`} /></td>
                    <td><StatusPill status={row.status} testId={`board-status-${row.id}`} /></td>
                    <td>{row.status === "Filled" && !row.assigned_recruiter ? <Dash testId={`board-recruiter-${row.id}`} /> : <UnassignedCell label="Unassigned" testId={`board-recruiter-${row.id}`} value={row.assigned_recruiter} />}</td>
                    <td><UnassignedCell label="Unassigned" testId={`board-candidate-${row.id}`} value={row.assigned_candidate} /></td>
                    <td>{row.needed_date ? formatDate(row.needed_date) : <Dash />}</td>
                    <td><AgeCell row={row} testId={`board-age-${row.id}`} /></td>
                    <td>{row.last_updated ? formatDate(row.last_updated) : <Dash />}</td>
                  </>
                )}
                <td className="board-menu-cell">
                  {(!archived || canDelete) && <RowMenu archived={archived} canDelete={canDelete} canManage={canManage} row={row} onAction={onAction} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <div className="board-empty" data-testid={archived ? "staffing-board-archived-empty" : "staffing-board-empty"}>
            {archived ? "No archived requests. Anything archived from the board appears here and can be restored or permanently deleted." : "No requests match these filters."}
          </div>
        )}
      </div>
    </section>
  );
};
